/** Utility class to help with built-in browser properties (eg. window.location) */
class Browser {
  /**
   * Retrieve the window.location property
   */
  getLocation(): Location {
    return window.location
  }

  /**
   * Go to a particular page
   * @param location - Location to redirect to
   * @param excludeFromHistory - whether we should exclude this move from the browser
   *    history (use sparingly)
   */
  go(location: string, excludeFromHistory = false): void {
    if (excludeFromHistory) {
      window.location.replace(location)
    } else {
      window.location.href = location
    }
  }

  /**
   * Reload the current page
   */
  reload(): void {
    window.location.reload()
  }

  /**
   * Check if the browser's user agent is Android
   * @return whether your browser is on Android or not
   */
  isAndroid(): boolean {
    const agent = navigator.userAgent || navigator.vendor

    if (agent.includes('android') || agent.includes('Android')) {
      return true
    }

    return false
  }

  /**
   * Check if the browser's user agent is iPhone
   * @return whether your browser is iPhone
   */
  isiPhone(): boolean {
    const agent = navigator.userAgent || navigator.vendor

    if (agent.includes('iphone') || agent.includes('iPhone')) {
      return true
    }

    return false
  }

  /**
   * Check if the browser's user agent is iPad
   * @return whether your browser is iPad
   */
  isiPad(): boolean {
    const agent = navigator.userAgent || navigator.vendor

    if (agent.includes('ipad') || agent.includes('iPad')) {
      return true
    }

    return false
  }

  /**
   * Check if the browser's user agent indicates a mobile device
   * @return whether your browser is mobile
   */
  isMobile(): boolean {
    return this.isAndroid() || this.isiPhone() || this.isiPad()
  }

  /**
   * Tests if this is a webview open in one of our mobile apps.
   * Should work for onx owned webviews as well as Braze.
   */
  isIOSWebView(): boolean {
    //return true
    return (
      this.isLegacyAppUserAgent() ||
      this.isOnxAppUserAgent() ||
      this.isIOSGenericWebview()
    )
  }

  /**
   * Matches user agent such as `com.onxmaps.backcountry/#####`
   * @see https://github.com/onXmaps/architecture/blob/master/architecture/context.md#webview-user-agent
   */
  isOnxAppUserAgent(): boolean {
    return /^com\.onxmaps\./.test(navigator.userAgent.toLowerCase())
  }

  /**
   * Matches user agent such as `Mobile/Version/#### {{App}}/####`
   * Used in mobile client webviews, will be superseded by onx user agent
   */
  isLegacyAppUserAgent(): boolean {
    return /^mobile\/version\//.test(navigator.userAgent.toLowerCase())
  }

  /**
   * Matches webviews opened by Braze, eg.
   */
  isIOSGenericWebview(): boolean {
    return (
      navigator.userAgent.toLowerCase().includes('mobile/') &&
      !navigator.userAgent.toLowerCase().includes('safari/') &&
      (this.isiPhone() || this.isiPad())
    )
  }

  /**
   * Check if the browser's user agent is Firefox
   * @return whether your browser is Firefox
   */
  isFirefox(): boolean {
    return navigator.userAgent.search('Firefox') !== -1
  }

  /**
   * Check if the browser's user agent is IE
   * @return whether your browser is IE
   */
  isIE(): boolean {
    return 'msSaveBlob' in window.navigator && !this.isEdge()
  }

  /**
   * Check if the browser's user agent is Edge
   * @return whether your browser is Edge
   */
  isEdge(): boolean {
    return navigator.userAgent.indexOf('Edge') >= 0
  }

  /**
   * Check if the browser's user agent is Chrome
   * @return whether your browser is Chrome
   */
  isChrome(): boolean {
    return (
      navigator.userAgent.indexOf('Chrome') >= 0 &&
      navigator.userAgent.indexOf('Edge') === -1
    )
  }

  /**
   * Check if the browser's user agent is Safari
   * @return whether your browser is Safari
   */
  isSafari(): boolean {
    return (
      navigator.userAgent.indexOf('Safari') >= 0 &&
      navigator.userAgent.indexOf('Chrome') === -1
    )
  }

  /**
   * Get the device info for a browser
   * @return info i.e. "ipad,safari"
   */
  deviceInfo(): string {
    const deviceInfo: string[] = []

    browser.isAndroid() && deviceInfo.push('android')
    browser.isiPhone() && deviceInfo.push('iphone')
    browser.isiPad() && deviceInfo.push('ipad')
    browser.isFirefox() && deviceInfo.push('firefox')
    browser.isIE() && deviceInfo.push('ie')
    browser.isEdge() && deviceInfo.push('edge')
    browser.isChrome() && deviceInfo.push('chrome')
    browser.isSafari() && deviceInfo.push('safari')

    return deviceInfo.join(',')
  }
}

const browser = new Browser()
export default browser

/**
 * Wait for the tab to become focused before continuing
 * @return a promise that resolves when the tab is focused
 */
export function waitForFocus(): Promise<void> {
  return new Promise((resolve) => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        resolve()
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }

    // If the tab is already focused, resolve the promise immediately
    if (document.visibilityState === 'visible') {
      resolve()
    } else {
      // Otherwise, wait for the tab to become focused
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }
  })
}
